import React from 'react';
import { CheckCircle2, Circle, Lock } from 'lucide-react';
import type { SopMediaStatus, SopVideoOption } from '../../../request/sop-synthesizer/sopSynthesizer.api.ts';
import { formatDuration, formatWhen } from '../format.ts';

const BADGES: Record<SopMediaStatus, { label: string; style: string; help: string }> = {
  ready: { label: 'Ready', style: 'bg-emerald-50 text-emerald-700', help: '' },
  pending: {
    label: 'Media processing pending',
    style: 'bg-amber-50 text-amber-700',
    help: 'Your administrator prepares this video before it can be used.',
  },
  processing: {
    label: 'Media processing in progress',
    style: 'bg-indigo-50 text-indigo-700',
    help: 'Being prepared now. This page updates when it is ready.',
  },
  failed: {
    label: 'Media processing failed',
    style: 'bg-red-50 text-red-700',
    help: 'Preparation failed. Ask your administrator to retry it.',
  },
  expired: {
    label: 'Expired',
    style: 'bg-gray-100 text-gray-600',
    help: 'This video has passed its retention period and was removed.',
  },
};

interface Props {
  videos: SopVideoOption[];
  value: string;
  onChange: (id: string) => void;
}

const VideoPicker: React.FC<Props> = ({ videos, value, onChange }) => (
  <ul className="divide-y divide-gray-200 rounded-md border border-gray-300" role="radiogroup" aria-label="Source video">
    {videos.map((v) => {
      const badge = BADGES[v.media_status] ?? BADGES.pending;
      const usable = v.media_status === 'ready';
      const selected = value === v.id;
      return (
        <li key={v.id}>
          <label
            className={`flex items-start gap-3 p-3 text-sm ${usable ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed opacity-75'} ${selected ? 'bg-blue-50' : ''}`}
          >
            <input
              type="radio"
              name="sop-video"
              className="sr-only"
              checked={selected}
              disabled={!usable}
              onChange={() => onChange(v.id)}
            />
            <span className="mt-0.5" aria-hidden="true">
              {!usable ? <Lock className="h-4 w-4 text-gray-400" /> : selected ? <CheckCircle2 className="h-4 w-4 text-blue-600" /> : <Circle className="h-4 w-4 text-gray-400" />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-medium text-gray-900">{v.title}</span>
              <span className="block text-xs text-gray-500">
                {usable
                  ? `${formatDuration(v.duration_seconds)} · prepared ${formatWhen(v.processed_at)}`
                  : `Uploaded ${formatWhen(v.uploaded_at)}. ${badge.help}`}
              </span>
            </span>
            <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${badge.style}`}>{badge.label}</span>
          </label>
        </li>
      );
    })}
  </ul>
);

export default VideoPicker;
